import { Autocomplete, Loader } from '@mantine/core';
import { useCallback, useRef, useState } from 'react';

/** Photon API feature (simplified). */
interface PhotonFeature {
  type: string;
  properties: {
    name?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  geometry: { type: string; coordinates: [number, number] };
}

export interface AddressSuggestion {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  latitude?: number;
  longitude?: number;
}

const PHOTON_URL = 'https://photon.komoot.io/api/';
const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

function formatAddressLine(f: PhotonFeature): string {
  const p = f.properties;
  const parts: string[] = [];
  if (p.housenumber) parts.push(p.housenumber);
  if (p.street) parts.push(p.street);
  return parts.join(' ').trim() || p.name || '';
}

function formatSuggestionLabel(f: PhotonFeature): string {
  const p = f.properties;
  const street = formatAddressLine(f) || p.name;
  const city = p.city ?? '';
  const state = p.state ?? '';
  const zip = p.postcode ?? '';
  const parts = [street, [city, state, zip].filter(Boolean).join(', ')].filter(Boolean);
  return parts.join(' — ') || 'Address';
}

async function searchPhoton(
  query: string,
  signal: AbortSignal
): Promise<PhotonFeature[]> {
  const params = new URLSearchParams({
    q: query,
    limit: '8',
    lang: 'en',
  });
  const res = await fetch(`${PHOTON_URL}?${params}`, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data?.features ?? [];
}

export interface AddressSearchInputProps {
  label?: string;
  placeholder?: string;
  /** Controlled value (e.g. street address line 1). When provided, input is controlled. */
  value?: string;
  /** Called when the text changes (e.g. user types). Use with value for controlled mode. */
  onChange?: (value: string) => void;
  onSelect: (address: AddressSuggestion) => void;
  disabled?: boolean;
  /** Hide the "Type at least N characters" description when true. */
  hideDescription?: boolean;
  /** Show the required-field asterisk next to the label. */
  withAsterisk?: boolean;
}

export function AddressSearchInput({
  label = 'Search address',
  placeholder = 'Start typing an address...',
  value: controlledValue,
  onChange: onControlledChange,
  onSelect,
  disabled = false,
  hideDescription = false,
  withAsterisk = false,
}: AddressSearchInputProps): JSX.Element {
  const [internalValue, setInternalValue] = useState('');
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const setValue = useCallback(
    (v: string) => {
      if (controlledValue === undefined) setInternalValue(v);
      onControlledChange?.(v);
    },
    [controlledValue, onControlledChange]
  );
  const [options, setOptions] = useState<{ label: string; feature: PhotonFeature }[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const runSearch = useCallback((query: string) => {
    if (abortRef.current) abortRef.current.abort();
    if (query.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    abortRef.current = new AbortController();
    searchPhoton(query.trim(), abortRef.current.signal)
      .then((features) => {
        const items = features.map((f) => ({
          label: formatSuggestionLabel(f),
          feature: f,
        }));
        setOptions(items);
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setOptions([]);
      })
      .finally(() => {
        setLoading(false);
        abortRef.current = null;
      });
  }, []);

  const handleChange = useCallback(
    (v: string) => {
      setValue(v);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (v.length < MIN_QUERY_LENGTH) {
        setOptions([]);
        return;
      }
      debounceRef.current = setTimeout(() => runSearch(v), DEBOUNCE_MS);
    },
    [runSearch, setValue]
  );

  const handleOptionSubmit = useCallback(
    (optionValue: string) => {
      const idx = parseInt(optionValue.replace(/^__addr_/, ''), 10);
      const item = Number.isNaN(idx) ? undefined : options[idx];
      if (!item) return;
      const p = item.feature.properties;
      const [lon, lat] = item.feature.geometry?.coordinates ?? [];
      // Use street-only for address line 1; avoid putting full "Street — City, State Zip" in the field
      let addressLine = formatAddressLine(item.feature);
      if (!addressLine && p.name) {
        // If API only gave a name, take the part before " — " or first segment before comma
        const name = p.name;
        addressLine = name.includes(' — ') ? name.split(' — ')[0].trim() : name.split(',')[0].trim();
      }
      addressLine = addressLine || '';
      onSelect({
        address: addressLine,
        city: p.city ?? '',
        state: p.state ?? '',
        zipCode: p.postcode ?? '',
        latitude: lat,
        longitude: lon,
      });
      setValue(addressLine);
      setOptions([]);
    },
    [options, onSelect, setValue]
  );

  // Unique option values (Mantine disallows duplicates); label is the display text.
  const dataOptions = options.map((o, i) => ({ value: `__addr_${i}`, label: o.label }));

  return (
    <Autocomplete
      label={label}
      withAsterisk={withAsterisk}
      placeholder={placeholder}
      description={hideDescription ? undefined : `Type at least ${MIN_QUERY_LENGTH} characters to search real addresses`}
      value={value}
      onChange={handleChange}
      data={dataOptions}
      onOptionSubmit={handleOptionSubmit}
      disabled={disabled}
      rightSection={loading ? <Loader size="xs" /> : undefined}
      filter={({ options }) => options}
      styles={{
        label: {
          fontSize: 'var(--mantine-font-size-md)',
        },
      }}
    />
  );
}
